import { useNavigate } from "react-router-dom";
import { Crown, Sparkles, Zap, FlaskConical } from "lucide-react";
import { useCurrentPlan } from "@/hooks/useCurrentPlan";
import { useSampleDataStatus } from "@/hooks/useSampleData";
import { useLanguage } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";

/**
 * Plan indicator pill shown in the TopBar between TourGuide and the
 * AccountCluster. Reflects 4 states:
 *   - demo (empty)
 *   - demo (sample data)
 *   - free
 *   - pro
 *   - elite
 *
 * Visual size matches the GlobalClock / AccountCard (h-16) so the
 * top bar stays vertically aligned.
 */
export function PlanIndicator() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const fa = language === "fa";
  const { resolvedPlanCode, loading } = useCurrentPlan();
  const { data: sampleStatus } = useSampleDataStatus();

  if (loading) return null;

  const isDemo = resolvedPlanCode === "demo";
  const sampleActive = sampleStatus?.hasSample === true;

  // Compute display label + style per state
  let label: string;
  let sublabel: string | null = null;
  let Icon = Sparkles;
  let toneClass = "border-border bg-card text-muted-foreground";

  if (isDemo && sampleActive) {
    label = fa ? "دمو" : "Demo";
    sublabel = fa ? "داده نمونه" : "Sample Data";
    Icon = FlaskConical;
    toneClass = "border-amber-500/40 bg-amber-500/10 text-amber-500";
  } else if (isDemo) {
    label = fa ? "دمو" : "Demo";
    sublabel = fa ? "آزمایشی" : "Trial";
    Icon = Sparkles;
    toneClass = "border-sky-500/40 bg-sky-500/10 text-sky-500";
  } else if (resolvedPlanCode === "free") {
    label = fa ? "رایگان" : "Free";
    Icon = Sparkles;
    toneClass = "border-border bg-muted/40 text-muted-foreground";
  } else if (resolvedPlanCode === "pro") {
    label = "Pro";
    Icon = Zap;
    toneClass = "border-primary/40 bg-primary/10 text-primary";
  } else if (resolvedPlanCode === "elite") {
    label = "Elite";
    Icon = Crown;
    toneClass = "border-yellow-500/40 bg-yellow-500/10 text-yellow-500";
  } else {
    label = resolvedPlanCode;
  }

  return (
    <button
      type="button"
      onClick={() => navigate("/account/billing")}
      className={cn(
        "hidden lg:flex flex-col items-center justify-center gap-0.5 h-16 px-3 rounded-md border transition-colors hover:opacity-90 w-fit",
        toneClass,
      )}
      title={fa ? "پلن فعال شما" : "Your active plan"}
    >
      <span className="uppercase tracking-wide text-muted-foreground/80 leading-none text-xs font-mono font-bold">
        {fa ? "پلن" : "َActive Plan"}
      </span>
      <div className="flex items-center gap-1.5 leading-none mt-1 text-sm font-bold">
        <Icon className="w-3.5 h-3.5" />
        <span className="leading-none text-base font-bold">{label}</span>
      </div>
      {sublabel && (
        <span className="text-[10px] font-medium leading-none mt-1">{sublabel}</span>
      )}
    </button>
  );
}
